import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getUser } from '../utils/auth';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export function periodOwnedByCalculation(migration, period) {
  const active = migration?.active;
  if (!active || !period || active.cutover_year == null) return false;
  const year = Number(period.year);
  const month = Number(period.month);
  return year > active.cutover_year || (year === active.cutover_year && month >= active.cutover_month);
}

const money = (value) => Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const ProjectInsuranceMigration = ({ projectVuid, inPreview, onMigration }) => {
  const [status, setStatus] = useState(null);
  const [periodVuid, setPeriodVuid] = useState('');
  const [comparison, setComparison] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${baseURL}/api/projects/${projectVuid}/gl-insurance/migration`)
      .then((response) => {
        if (cancelled) return;
        setStatus(response.data);
        if (onMigration) onMigration(response.data);
        if (response.data.suggested_cutover_period_vuid) {
          setPeriodVuid(response.data.suggested_cutover_period_vuid);
        }
      })
      .catch(() => {
        if (!cancelled) setMessage('Could not load the Project Calculations move.');
      });
    return () => { cancelled = true; };
  }, [projectVuid]);

  const calculationPath = status?.active
    ? (inPreview
      ? `/procore/projects/${projectVuid}/calculations/${status.active.assignment_vuid}`
      : `/projects/${projectVuid}/calculations/${status.active.assignment_vuid}`)
    : '';

  const compare = async () => {
    setBusy(true);
    setMessage('');
    setComparison(null);
    try {
      const response = await axios.post(
        `${baseURL}/api/projects/${projectVuid}/gl-insurance/migration/compare`,
        { accounting_period_vuid: periodVuid },
      );
      setComparison(response.data);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not compare GL Insurance.');
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    if (!window.confirm(
      'Move future GL Insurance to Project Calculations? Existing GL Insurance history will not change.',
    )) return;
    setBusy(true);
    setMessage('');
    try {
      const response = await axios.post(`${baseURL}/api/projects/${projectVuid}/gl-insurance/migration`, {
        accounting_period_vuid: periodVuid,
        confirmed: true,
        acknowledge_differences: acknowledged
          ? (comparison?.acknowledgements || []).map((item) => item.code)
          : [],
        acknowledge_existing_runs: true,
      });
      setStatus(response.data);
      setComparison(null);
      setConfirmed(false);
      if (onMigration) onMigration(response.data);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not move GL Insurance.');
    } finally {
      setBusy(false);
    }
  };

  const rollback = async () => {
    if (!window.confirm('Return this project to Needs Migration? GL Insurance will not post until it is moved again.')) return;
    setBusy(true);
    setMessage('');
    try {
      const response = await axios.post(`${baseURL}/api/projects/${projectVuid}/gl-insurance/migration/rollback`);
      setStatus(response.data);
      if (onMigration) onMigration(response.data);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not return this project to Needs Migration.');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const proposal = status.proposal;
  const active = status.active;
  const selectable = (status.periods || []).filter((period) => period.selectable);
  const canChange = (getUser()?.role || 'user') !== 'viewer';

  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-3">
      {message ? <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{message}</div> : null}
      {active ? (
        <div>
          <h2 className="text-base font-semibold text-gray-900">Moved to Project Calculations</h2>
          <p className="text-sm text-gray-700 mt-1">
            Effective {active.cutover_label}. Historical GL Insurance stays on this page and is not rewritten.
            {active.created_by ? ` Moved by ${active.created_by}.` : ''}
            {active.version_number ? ` Generic version ${active.version_number}.` : ''}
            {active.comparison_summary && active.comparison_summary.match === true ? ' The comparison matched.' : ''}
            {active.comparison_summary && active.comparison_summary.match === false ? ' The comparison difference was acknowledged.' : ''}
            {active.close_policy_note ? ` ${active.close_policy_note}` : ''}
          </p>
          <div className="flex flex-wrap gap-3 mt-3">
            <Link to={calculationPath} className="btn-claret inline-flex items-center">Open Project Calculation</Link>
            {active.rollback_allowed && canChange ? (
              <button type="button" className="btn-claret" onClick={rollback} disabled={busy}>Return to Needs Migration</button>
            ) : null}
            {active.rollback_block ? (
              <p className="text-sm text-gray-700">{active.rollback_block}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      {!active && status.eligible && proposal ? (
        <div>
          <h2 className="text-base font-semibold text-gray-900">Move to Project Calculations</h2>
          <p className="text-sm text-gray-700 mt-1">
            This moves future GL Insurance calculations to the Project Calculation Engine. Existing GL Insurance history will remain unchanged.
          </p>
          <ul className="text-sm text-gray-800 mt-3 space-y-1">
            <li>Treatment: {proposal.treatment_label}</li>
            <li>Insurance rate: {proposal.insurance_rate} per $1,000</li>
            <li>Accrual rate: {proposal.accrual_rate} per $1,000</li>
            <li>Destinations: {proposal.destination_count}</li>
            <li>Source pairs: {proposal.source_count}</li>
            <li>Month-end behavior: {proposal.close_policy_label}</li>
          </ul>
          <p className="text-sm text-gray-600 mt-2">{proposal.close_policy_note}</p>
          <label className="block text-sm font-medium text-gray-700 mt-3">
            Project Calculations begins
            <select className="mt-1 w-full md:w-64 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" value={periodVuid} onChange={(event) => { setPeriodVuid(event.target.value); setComparison(null); }}>
              <option value="">Select</option>
              {selectable.map((period) => (
                <option key={period.vuid} value={period.vuid}>{period.label}</option>
              ))}
            </select>
          </label>
          {canChange ? (
            <button type="button" className="btn-claret mt-3" onClick={compare} disabled={busy || !periodVuid}>Compare Before Migration</button>
          ) : (
            <p className="text-sm text-gray-700 mt-3">An accounting user must move this setup.</p>
          )}
        </div>
      ) : null}
      {!active && status.issues?.length ? (
        <div className="text-sm text-amber-900">
          {status.issues.map((issue) => <div key={issue}>{issue}</div>)}
        </div>
      ) : null}
      {comparison ? (
        <div className="border-t border-gray-200 pt-3">
          <table className="budget-lines-table w-full text-sm">
            <thead>
              <tr>
                <th></th>
                <th>Legacy</th>
                <th>Project Calculation</th>
              </tr>
            </thead>
            <tbody>
              {['eligible', 'premium', 'accrual', 'recovery'].map((field) => (
                <tr key={field}>
                  <td className="capitalize">{field === 'eligible' ? 'Eligible billing' : field}</td>
                  <td>{money(comparison.legacy[field])}</td>
                  <td>{money(comparison.calculation[field])}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={`text-sm mt-3 ${comparison.match ? 'text-green-800' : 'text-amber-900'}`}>
            {comparison.match ? 'Accounting matches.' : 'Differences require review.'}
          </p>
          {(comparison.differences || []).map((item) => <p key={item} className="text-sm text-red-700">{item}</p>)}
          {(comparison.acknowledgements || []).map((item) => <p key={item.code} className="text-sm text-amber-900">{item.message}</p>)}
          {(comparison.destinations || []).filter((item) => item.note).map((item) => (
            <p key={item.line_number} className="text-sm text-amber-900">{item.label}: {item.note}</p>
          ))}
          {comparison.requires_acknowledgement ? (
            <label className="flex items-start gap-2 text-sm text-gray-800 mt-3">
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
              <span>I understand this legacy difference and still want to move future calculations.</span>
            </label>
          ) : null}
          <label className="flex items-start gap-2 text-sm text-gray-800 mt-3">
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
            <span>
              Historical insurance accounting will not change. Legacy GL Insurance stops owning periods beginning with the cutover.
              Project Calculations becomes authoritative. Future insurance must be calculated and reviewed before close can post it.
              Reversal and correction will use Project Calculation behavior.
            </span>
          </label>
          {canChange ? (
            <button
              type="button"
              className="btn-claret mt-3"
              disabled={busy || !confirmed || (comparison.requires_acknowledgement && !acknowledged) || comparison.differences?.length}
              onClick={activate}
            >
              Move to Project Calculations
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ProjectInsuranceMigration;
